import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brandingConfig";
import { Loader2, ShieldCheck, Lock, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import "./AdminPartnerRecovery.css";

/**
 * Partner Portal — Set Password page.
 *
 * Reached from the "Force Password Reset" email link:
 *   {base}/partner-portal/set-password?t=<plaintext-token>&u=<partner-user-id>
 *
 * The token is a SHA-256 hash lookup against `partner_users.password_reset_token_hash`
 * with a 24h TTL. Public: no auth required. Consuming the token invalidates
 * all existing portal sessions for that user and writes an audit row.
 */
const MIN_LEN = 12;

function scorePassword(pw) {
  return {
    length:    pw.length >= MIN_LEN,
    lowercase: /[a-z]/.test(pw),
    uppercase: /[A-Z]/.test(pw),
    digit:     /\d/.test(pw),
    symbol:    /[^A-Za-z0-9]/.test(pw),
  };
}

export default function PartnerSetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("t") || "";
  const userId = sp.get("u") || "";

  const [status, setStatus] = useState("checking"); // checking | ready | invalid | submitting | done
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!token || !userId) {
      setStatus("invalid");
      setErr("This link is missing required information. Please use the link from your HarborWell email.");
      return;
    }
    api.post("/partner-recovery/reset-password/verify", { token, user_id: userId })
      .then((r) => { setCtx(r.data); setStatus("ready"); })
      .catch((e) => { setStatus("invalid"); setErr(formatApiError(e)); });
  }, [token, userId]);

  const rules = scorePassword(pw);
  const allOk = Object.values(rules).every(Boolean) && pw === pw2 && pw.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setStatus("submitting");
    setErr(null);
    try {
      await api.post("/partner-recovery/reset-password/complete", {
        token, user_id: userId, new_password: pw,
      });
      setStatus("done");
    } catch (ex) {
      setStatus("ready");
      setErr(formatApiError(ex));
    }
  };

  return (
    <div className="pl" data-testid="partner-set-password-page">
      <div className="pl__card">
        <div className="pl__hero" style={{ background: "#1E3A8A", color: "#EFF6FF" }}>
          <img
            src={BRAND.logo}
            alt={BRAND.logoAlt}
            data-testid="partner-set-password-brand-logo"
            style={{ height: 40, width: "auto", background: "#FFFFFF", padding: 4, borderRadius: 6 }}
          />
          <h1>Set your Partner Portal password</h1>
        </div>

        <div className="pl__body">
          {status === "checking" && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem",
                            color: "#475569", padding: "1rem 0" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying secure link…
            </div>
          )}

          {status === "invalid" && (
            <div data-testid="partner-set-password-error"
                 style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start",
                            background: "#FEF2F2", border: "1px solid #FCA5A5",
                            padding: "0.85rem 1rem", borderRadius: 10 }}>
              <XCircle className="w-5 h-5" style={{ color: "#991B1B", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, color: "#991B1B" }}>Link cannot be used</div>
                <div style={{ fontSize: "0.85rem", color: "#7F1D1D", marginTop: "0.3rem" }}>
                  {err}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#475569", marginTop: "0.6rem" }}>
                  Contact HarborWell Support to request a new secure link.
                </div>
              </div>
            </div>
          )}

          {(status === "ready" || status === "submitting") && ctx && (
            <>
              <div className="pl__partner">
                <div className="pl__partner-label">Account</div>
                <div className="pl__partner-name" data-testid="partner-set-password-email">
                  {ctx.name || ctx.email}
                </div>
                <div style={{ fontSize: "0.75rem", color: "#64748B", marginTop: "0.2rem" }}>
                  {ctx.email}{ctx.partner_org_name ? ` · ${ctx.partner_org_name}` : ""}
                </div>
              </div>

              <p className="pl__desc">
                Choose a strong new password. This link can only be used once.
                All active portal sessions for this account will be signed out.
              </p>

              <form onSubmit={submit} data-testid="partner-set-password-form">
                <label style={{ display: "block", marginTop: "0.9rem",
                                  fontSize: "0.78rem", color: "#334155", fontWeight: 600 }}>
                  New password
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={show ? "text" : "password"}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                    minLength={MIN_LEN}
                    maxLength={256}
                    data-testid="partner-set-password-input"
                    style={{ width: "100%", padding: "0.6rem 2.4rem 0.6rem 0.75rem",
                                border: "1px solid #CBD5E1", borderRadius: 8,
                                fontSize: "0.9rem", fontFamily: "inherit", marginTop: "0.35rem" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="partner-set-password-toggle"
                    style={{ position: "absolute", right: "0.5rem", top: "0.7rem",
                                background: "transparent", border: "none", cursor: "pointer",
                                color: "#64748B" }}
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <label style={{ display: "block", marginTop: "0.75rem",
                                  fontSize: "0.78rem", color: "#334155", fontWeight: 600 }}>
                  Confirm new password
                </label>
                <input
                  type={show ? "text" : "password"}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  autoComplete="new-password"
                  minLength={MIN_LEN}
                  maxLength={256}
                  data-testid="partner-set-password-confirm"
                  style={{ width: "100%", padding: "0.6rem 0.75rem",
                              border: "1px solid #CBD5E1", borderRadius: 8,
                              fontSize: "0.9rem", fontFamily: "inherit", marginTop: "0.35rem" }}
                />

                <ul data-testid="partner-set-password-rules"
                    style={{ listStyle: "none", padding: 0, margin: "0.9rem 0 0",
                                fontSize: "0.78rem", color: "#334155", lineHeight: 1.55 }}>
                  {[
                    ["length",    `At least ${MIN_LEN} characters`],
                    ["lowercase", "One lowercase letter"],
                    ["uppercase", "One uppercase letter"],
                    ["digit",     "One digit"],
                    ["symbol",    "One symbol (!@#$…)"],
                  ].map(([k, label]) => (
                    <li key={k} style={{ display: "flex", alignItems: "center", gap: "0.4rem",
                                          color: rules[k] ? "#166534" : "#64748B" }}>
                      {rules[k]
                        ? <CheckCircle2 className="w-3.5 h-3.5" />
                        : <span style={{ width: 14, height: 14, borderRadius: 999,
                                          border: "1.5px solid #CBD5E1", display: "inline-block" }} />}
                      {label}
                    </li>
                  ))}
                  {pw2.length > 0 && (
                    <li style={{ display: "flex", alignItems: "center", gap: "0.4rem",
                                    color: pw === pw2 ? "#166534" : "#991B1B" }}>
                      {pw === pw2
                        ? <CheckCircle2 className="w-3.5 h-3.5" />
                        : <XCircle className="w-3.5 h-3.5" />}
                      Passwords match
                    </li>
                  )}
                </ul>

                {err && (
                  <div style={{ marginTop: "0.85rem", background: "#FEF2F2",
                                  border: "1px solid #FCA5A5", padding: "0.6rem 0.75rem",
                                  borderRadius: 8, color: "#991B1B", fontSize: "0.82rem" }}
                       data-testid="partner-set-password-server-error">
                    {err}
                  </div>
                )}

                <div className="pl__actions">
                  <button
                    type="submit"
                    disabled={!allOk || status === "submitting"}
                    data-testid="partner-set-password-submit"
                    className="pr-btn pr-btn--primary"
                  >
                    {status === "submitting"
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <ShieldCheck className="w-4 h-4" />}
                    Update password
                  </button>
                </div>
              </form>
            </>
          )}

          {status === "done" && (
            <div data-testid="partner-set-password-done"
                 style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start",
                            background: "#F0FDF4", border: "1px solid #86EFAC",
                            padding: "0.85rem 1rem", borderRadius: 10 }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: "#166534", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, color: "#166534" }}>Password updated</div>
                <div style={{ fontSize: "0.85rem", color: "#166534", marginTop: "0.3rem" }}>
                  You can now sign in with your new password.
                </div>
                <div style={{ marginTop: "0.85rem" }}>
                  <Link to="/partner/login"
                        className="pr-btn pr-btn--primary"
                        data-testid="partner-set-password-signin"
                        onClick={() => setTimeout(() => nav("/partner/login"), 0)}>
                    Go to Partner Portal sign in
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="pl__note">
            No credentials, MFA codes, or personal information are transmitted through
            this page. Contact HarborWell Support if you did not request this reset.
          </div>
        </div>
      </div>
    </div>
  );
}
